import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { Organisation, PaginatedOrganisationList } from "@nens/threedi/openapi-client";

import { getContractApi, getOrganisationsApi, getStatusApi } from "../apiConfiguration";
import { AuthContext } from "../providers/AuthProvider";
import { OrganisationContext } from "../providers/OrganisationProvider";

const statusApi = getStatusApi();
const organisationsApi = getOrganisationsApi();
const contractsApi = getContractApi();

// Returns current contract of the selected organisation
export function useOrganisationContract(uniqueId: string) {
  const contractsListResponse = useQuery({
    queryKey: ["contractslist", uniqueId],
    queryFn: () => contractsApi.contractsList({ organisationUniqueId: uniqueId }),
  });

  if (contractsListResponse.isSuccess) {
    return contractsListResponse.data.results.length > 0
      ? contractsListResponse.data.results[0]
      : null;
  } else {
    return null;
  }
}

// Returns current HCC framework version on the backend, or null
// if the request is not finished yet.
export function useThreediVersion(): string | null {
  const statusResponse = useQuery({
    queryKey: ["statusCurrentVersion"],
    queryFn: () => statusApi.statusCurrentVersion(),
  });

  if (statusResponse.isSuccess) {
    return statusResponse.data?.threediVersion || null;
  } else {
    return null;
  }
}

// Returns user roles in current organisation, empty if response isn't loaded yet
export function useUserRoles(): string[] {
  const organisationContext = useContext(OrganisationContext);
  const userContext = useContext(AuthContext);

  const rolesResponse = useQuery({
    queryKey: [
      "organisationuser",
      organisationContext.organisation.uniqueId,
      userContext.profile.username,
    ],
    queryFn: () =>
      organisationsApi.organisationsUsersRead({
        id: userContext.profile.id ?? 0,
        uniqueId: organisationContext.organisation.uniqueId,
      }),
  });

  if (rolesResponse.isSuccess) {
    return rolesResponse.data.roles || [];
  } else {
    return [];
  }
}

export function useUserHasRole(role: string): boolean {
  return useUserRoles().indexOf(role) !== -1;
}

const needsCreatorRoleTitle =
  "This action requires the Creator role. Please contact someone in your organisation with Manager rights to grant you the required privileges.";

export function useNeedsCreatorRoleTitle(): string | undefined {
  return useUserHasRole("creator") ? undefined : needsCreatorRoleTitle;
}

export function useOrganisationManagers() {
  const organisationsApi = getOrganisationsApi();
  const { organisation } = useContext(OrganisationContext);

  const managersResponse = useQuery({
    queryKey: ["organisationmanagers", organisation.uniqueId],
    queryFn: () =>
      organisationsApi.organisationsUsersList({
        uniqueId: organisation.uniqueId,
      }),
  });

  if (managersResponse.isSuccess) {
    return managersResponse.data.results.filter(
      (user) => user && user.roles && user.roles.includes("manager")
    );
  } else {
    return [];
  }
}

// IN PROGRESS IMPORTANT: STILL WAIT
// Returns user roles in current organisation, empty if response isn't loaded yet
export function getOrganisationWithUserRoles(roles: string[]): Organisation[] {
  const orgainisationsResponse: UseQueryResult<PaginatedOrganisationList, Error>[] = [];
  roles.forEach((role) =>
    orgainisationsResponse.push(
      useQuery({
        queryKey: ["organisations", role],
        queryFn: () =>
          organisationsApi.organisationsList({
            validContractsOnly: true,
            loggedInUserHasRole: role,
            limit: 9999,
          }),
      })
    )
  );
  if (orgainisationsResponse.every((response) => response.isSuccess)) {
    return orgainisationsResponse
      .flatMap((instance: UseQueryResult<PaginatedOrganisationList>) =>
        instance.data ? instance.data.results : []
      )
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => t && value && t.uniqueId === value.uniqueId)
      );
  } else {
    return [];
  }
}
